import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
// Using Info icon from lucide-react instead of InfoCircledIcon from radix
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Activity, AlertTriangle, CheckCircle, Download, FileText, Info, Loader2, Stethoscope, UserCheck, User, Calendar, Clock, Heart, Thermometer, Wind, ArrowRight } from "lucide-react";
import { format } from "date-fns";
import { useAuth } from "@/hooks/useAuth";
import { TriageExplanationPanel } from "./TriageExplanationPanel";

interface TriageDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  triageId: string | null;
}

type VitalSign = {
  name: string;
  value: string | number;
  unit: string;
  status: 'normal' | 'warning' | 'critical' | 'unknown';
  icon?: React.ReactNode;
};

type Doctor = {
  id: string;
  name: string;
};

const formatVitalSigns = (vitals: any) => {
  const vitalSigns: VitalSign[] = [];
  
  if (vitals?.temperature) {
    const temp = parseFloat(vitals.temperature);
    const isFahrenheit = temp > 45; // Same heuristic as in MTS rules.ts
    const tempCelsius = isFahrenheit ? (temp - 32) * 5/9 : temp;
    
    let status: VitalSign['status'] = 'normal';
    // Evaluate against Celsius thresholds
    if (tempCelsius > 38.5) status = 'critical';
    else if (tempCelsius > 38.0 || tempCelsius < 36.0) status = 'warning';
    
    vitalSigns.push({
      name: 'Temperature',
      value: isFahrenheit ? temp : tempCelsius,
      unit: isFahrenheit ? '°F' : '°C',
      status,
      icon: <Thermometer className="h-5 w-5" />
    });
  }
  
  if (vitals?.heart_rate) {
    const hrValue = parseInt(vitals.heart_rate);
    let status: VitalSign['status'] = 'normal';
    
    if (hrValue > 120 || hrValue < 50) status = 'critical';
    else if (hrValue > 100 || hrValue < 60) status = 'warning';
    
    vitalSigns.push({
      name: 'Heart Rate',
      value: hrValue,
      unit: 'bpm',
      status,
      icon: <Heart className="h-5 w-5" />
    });
  }
  
  if (vitals?.respiratory_rate) {
    const rrValue = parseInt(vitals.respiratory_rate);
    let status: VitalSign['status'] = 'normal';
    
    if (rrValue > 30 || rrValue < 8) status = 'critical';
    else if (rrValue > 24 || rrValue < 12) status = 'warning';
    
    vitalSigns.push({
      name: 'Respiratory Rate',
      value: rrValue,
      unit: 'rpm',
      status,
      icon: <Wind className="h-5 w-5" />
    });
  }
  
  if (vitals?.oxygen_saturation) {
    const o2Value = parseInt(vitals.oxygen_saturation);
    let status: VitalSign['status'] = 'normal';
    
    if (o2Value < 90) status = 'critical';
    else if (o2Value < 94) status = 'warning';
    
    vitalSigns.push({
      name: 'Oxygen Saturation',
      value: o2Value,
      unit: '%',
      status,
      icon: <Activity className="h-5 w-5" />
    });
  }
  
  return vitalSigns;
};

export function TriageDetailsModal({ isOpen, onClose, triageId }: TriageDetailsModalProps) {
  const { profile } = useAuth();
  const [doctorNotes, setDoctorNotes] = useState('');
  const [selectedDoctor, setSelectedDoctor] = useState<string>('');
  const [isAssigning, setIsAssigning] = useState(false);
  
  // Fetch real doctors from the database
  const { data: doctors = [] } = useQuery<Doctor[]>({
    queryKey: ['clinic-doctors', profile?.clinic_id],
    queryFn: async () => {
      if (!profile?.clinic_id) return [];
      
      try {
        const { data, error } = await supabase
          .from('profiles')
          .select('id, first_name, last_name')
          .eq('clinic_id', profile.clinic_id)
          .eq('role', 'doctor');
          
        if (error) {
          console.error('Error fetching doctors:', error);
          return [];
        }
        
        return data.map((doctor: any) => ({
          id: doctor.id,
          name: `Dr. ${doctor.first_name} ${doctor.last_name}`
        }));
      } catch (err) {
        console.error('Exception in doctors query:', err);
        return [];
      }
    },
    enabled: !!profile?.clinic_id
  });
  
  const { data: triageDetails, isLoading } = useQuery({
    queryKey: ['triage-details', triageId],
    queryFn: async () => {
      if (!triageId || !profile?.clinic_id) return null;
      
      try {
        // Fetch triage submission details
        const { data: triageData, error: triageError } = await supabase
          .from('triage_submissions')
          .select('*')
          .eq('id', triageId)
          .eq('clinic_id', profile.clinic_id)
          .single();
        
        if (triageError) {
          console.error('Error fetching triage details:', triageError);
          return null;
        }
        
        if (!triageData) return null;
        
        // Fetch patient profile using the same proven RPC approach as Patient Management
        let patientData = null;
        
        try {
          console.log('DEBUG: TriageDetailsModal fetching patient data for:', triageData.patient_id);
          
          // Use the same RPC function that works in Patient Management
          const { data: allPatientsData, error: rpcError } = await supabase
            .rpc('get_patients_by_clinic', { search_query: '' });
          
          if (allPatientsData && !rpcError) {
            // Find the specific patient we need
            patientData = allPatientsData.find(patient => patient.id === triageData.patient_id);
            console.log('DEBUG: TriageDetailsModal found patient via RPC:', !!patientData);
          } else {
            console.error('DEBUG: TriageDetailsModal RPC failed:', rpcError?.message);
          }
          
          // Fallback: If RPC didn't return the patient, try direct profiles query
          if (!patientData) {
            console.log('DEBUG: TriageDetailsModal trying direct profiles query fallback');
            const { data: directPatientData, error: patientError } = await supabase
              .from('profiles')
              .select('*')
              .eq('id', triageData.patient_id)
              .single();
            
            if (directPatientData && !patientError) {
              patientData = directPatientData;
              console.log('DEBUG: TriageDetailsModal found patient via direct query');
            } else {
              console.error('DEBUG: TriageDetailsModal direct query also failed:', patientError?.message);
            }
          }
        } catch (err) {
          console.error('Error fetching patient details:', err);
        }
        
        // Initialize doctor notes if they exist
        if (triageData.doctor_notes) {
          setDoctorNotes(triageData.doctor_notes);
        }
        
        // Initialize selected doctor if one is assigned
        if (triageData.assigned_doctor_id) {
          setSelectedDoctor(triageData.assigned_doctor_id);
        }
        
        return {
          ...triageData,
          patient: patientData || { first_name: 'Unknown', last_name: 'Patient' }
        };
      } catch (err) {
        console.error('Exception in triage details query:', err);
        return null;
      }
    },
    enabled: !!triageId && isOpen && !!profile?.clinic_id
  });
  
  const getUrgencyColor = (urgency: string) => {
    switch (urgency) {
      case 'immediate': return 'bg-red-500 text-white';
      case 'urgent': return 'bg-orange-500 text-white';
      case 'standard': return 'bg-yellow-500 text-white';
      case 'non-urgent': return 'bg-green-500 text-white';
      default: return 'bg-gray-500 text-white';
    }
  };
  
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'submitted': return 'bg-blue-100 text-blue-800';
      case 'reviewed': return 'bg-green-100 text-green-800';
      case 'processed': return 'bg-purple-100 text-purple-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };
  
  const getVitalSignStatusColor = (status: VitalSign['status']) => {
    switch (status) {
      case 'critical': return 'text-red-600';
      case 'warning': return 'text-amber-600';
      case 'normal': return 'text-green-600';
      default: return 'text-gray-600';
    }
  };
  
  const getTriageActionRecommendation = (urgency: string) => {
    switch (urgency) {
      case 'immediate': return { action: 'Immediate medical intervention', time: 'Immediately' };
      case 'urgent': return { action: 'Urgent medical assessment', time: 'Within 10 minutes' };
      case 'standard': return { action: 'Standard medical assessment', time: 'Within 60 minutes' };
      case 'non-urgent': return { action: 'Non-urgent assessment', time: 'Within 120 minutes' };
      default: return { action: 'Assessment required', time: 'Timing undefined' };
    }
  };
  
  const handleSaveNotes = async () => {
    if (!triageId || !profile?.clinic_id) return;
    
    try {
      const { error } = await supabase
        .from('triage_submissions')
        .update({ doctor_notes: doctorNotes })
        .eq('id', triageId)
        .eq('clinic_id', profile.clinic_id);
      
      if (error) {
        console.error('Error saving doctor notes:', error);
        alert('Failed to save notes. Please try again.');
      } else {
        alert('Notes saved successfully');
      }
    } catch (err) {
      console.error('Exception saving doctor notes:', err);
      alert('An error occurred. Please try again.');
    }
  };
  
  const handleAssignDoctor = async () => {
    if (!triageId || !selectedDoctor) return;
    
    try {
      setIsAssigning(true);
      // Update triage submission with assigned doctor and status
      const { error } = await supabase
        .from('triage_submissions')
        .update({
          assigned_doctor_id: selectedDoctor,
          status: 'reviewed',
          reviewed_at: new Date().toISOString()
        })
        .eq('id', triageId);
      
      if (error) {
        console.error('Error assigning doctor:', error);
        return;
      }
      
      // Close modal and refresh data
      onClose();
    } catch (err) {
      console.error('Exception in assign doctor:', err);
    } finally {
      setIsAssigning(false);
    }
  };
  
  const handleDownloadSummary = () => {
    if (!triageDetails) return;
    
    const vitalSigns = formatVitalSigns(triageDetails.vitals);
    const recommendation = getTriageActionRecommendation(triageDetails.urgency);
    
    // Format the summary text
    let summary = `TRIAGE SUMMARY - ${format(new Date(triageDetails.created_at), 'MMM dd, yyyy h:mm a')}\n\n`;
    summary += `PATIENT: ${triageDetails.patient.first_name} ${triageDetails.patient.last_name}\n`;
    summary += `URGENCY: ${triageDetails.urgency.toUpperCase()}\n`;
    summary += `MANCHESTER TRIAGE CATEGORY: ${triageDetails.manchester_triage?.category || 'Not specified'}\n`;
    if (triageDetails.manchester_triage?.presenting_complaint) {
      summary += `PRESENTING COMPLAINT: ${triageDetails.manchester_triage.presenting_complaint}\n`;
    }
    if (triageDetails.manchester_triage?.discriminator) {
      summary += `DISCRIMINATOR: ${triageDetails.manchester_triage.discriminator}\n`;
    }
    summary += `\nRECOMMENDATION: ${recommendation.action} - Target time: ${recommendation.time}\n\n`;
    
    summary += 'VITAL SIGNS:\n';
    vitalSigns.forEach(vital => {
      summary += `- ${vital.name}: ${vital.value} ${vital.unit} (${vital.status})\n`;
    });
    
    summary += '\nSYMPTOMS:\n';
    triageDetails.symptoms.forEach(symptom => {
      summary += `- ${symptom}\n`;
    });
    
    if (triageDetails.red_flags && triageDetails.red_flags.length > 0) {
      summary += '\nRED FLAGS:\n';
      triageDetails.red_flags.forEach(flag => {
        summary += `- ${flag}\n`;
      });
    }
    
    if (doctorNotes) {
      summary += `\nDOCTOR NOTES:\n${doctorNotes}\n`;
    }
    
    // Create and download the file
    const blob = new Blob([summary], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `triage-summary-${triageDetails.id}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };
  
  if (!isOpen) return null;
  
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-6xl max-h-[90vh] w-[95vw] overflow-hidden">
        <DialogHeader className="pb-2 border-b">
          <div className="flex justify-between items-center">
            <DialogTitle className="text-2xl font-bold">Triage Assessment</DialogTitle>
            {triageDetails && (
              <Badge className={getUrgencyColor(triageDetails.urgency) + " px-3 py-1 text-md uppercase"}>
                {triageDetails.urgency}
              </Badge>
            )}
          </div>
        </DialogHeader>
        
        <ScrollArea className="h-[calc(90vh-10rem)] pr-4">
          {isLoading ? (
            <div className="text-center py-8">Loading triage details...</div>
          ) : !triageDetails ? (
            <div className="text-center py-8 text-red-600">
              <AlertTriangle className="w-8 h-8 mx-auto mb-2" />
              Failed to load triage details
            </div>
          ) : (
            <div className="space-y-6">
              {/* Patient and Triage Info - Enhanced clinical header */}
              <div className="bg-slate-50 rounded-lg p-4 border shadow-sm">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {/* Patient Info */}
                  <div className="space-y-2">
                    <h3 className="font-semibold text-sm uppercase text-gray-500 flex items-center gap-1">
                      <User className="w-4 h-4" /> Patient Information
                    </h3>
                    <div className="space-y-1">
                      <p className="text-xl font-bold">{triageDetails.patient.first_name} {triageDetails.patient.last_name}</p>
                      <p className="text-sm text-gray-600">{triageDetails.patient.email || 'No email recorded'}</p>
                      {triageDetails.patient.date_of_birth && (
                        <p className="text-sm flex items-center gap-1">
                          <Calendar className="h-3 w-3" /> 
                          DOB: {format(new Date(triageDetails.patient.date_of_birth), 'MMM dd, yyyy')}
                        </p>
                      )}
                    </div>
                  </div>
                  
                  {/* Triage Meta */}
                  <div className="space-y-2">
                    <h3 className="font-semibold text-sm uppercase text-gray-500 flex items-center gap-1">
                      <FileText className="w-4 h-4" /> Triage Details
                    </h3>
                    <div className="space-y-1">
                      <div className="flex items-center gap-2">
                        <Badge className={getStatusColor(triageDetails.status)}>
                          {triageDetails.status}
                        </Badge>
                        <p className="text-sm">{triageDetails.reviewed_by ? 'Reviewed' : 'Not reviewed'}</p>
                      </div>
                      <p className="text-sm flex items-center gap-1">
                        <Clock className="h-3 w-3" /> 
                        Submitted: {format(new Date(triageDetails.created_at), 'MMM dd, yyyy h:mm a')}
                      </p>
                      {triageDetails.updated_at && triageDetails.updated_at !== triageDetails.created_at && (
                        <p className="text-sm flex items-center gap-1">
                          <CheckCircle className="h-3 w-3" /> 
                          Last updated: {format(new Date(triageDetails.updated_at), 'MMM dd, yyyy h:mm a')}
                        </p>
                      )}
                    </div>
                  </div>
                  
                  {/* Action Info */}
                  <div className="space-y-2">
                    <h3 className="font-semibold text-sm uppercase text-gray-500 flex items-center gap-1">
                      <ArrowRight className="w-4 h-4" /> Required Action
                    </h3>
                    <div className="space-y-1">
                      {(() => {
                        const recommendation = getTriageActionRecommendation(triageDetails.urgency);
                        return (
                          <>
                            <p className="font-bold text-lg">{recommendation.action}</p>
                            <p className="text-amber-700 flex items-center gap-1">
                              <Clock className="h-4 w-4" />
                              Target time: {recommendation.time}
                            </p>
                          </>
                        );
                      })()}
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Manchester Triage Category - Enhanced clinical design */}
              {triageDetails.manchester_triage && (
                <Card className="border-l-4" style={{ borderLeftColor: getUrgencyColor(triageDetails.urgency).split(' ')[0].replace('bg-', '#') === 'bg-red-500' ? '#ef4444' : 
                                                       getUrgencyColor(triageDetails.urgency).split(' ')[0].replace('bg-', '#') === 'bg-orange-500' ? '#f97316' : 
                                                       getUrgencyColor(triageDetails.urgency).split(' ')[0].replace('bg-', '#') === 'bg-yellow-500' ? '#eab308' : 
                                                       getUrgencyColor(triageDetails.urgency).split(' ')[0].replace('bg-', '#') === 'bg-green-500' ? '#22c55e' : '#6b7280' }}>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Stethoscope className="h-5 w-5" />
                      Manchester Triage Assessment
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid md:grid-cols-2 gap-6">
                      <div className="space-y-3">
                        <div>
                          <h4 className="text-sm font-semibold uppercase text-gray-500 mb-1">Triage Category</h4>
                          <div className="flex items-center gap-2">
                            <Badge className={getUrgencyColor(triageDetails.urgency) + " py-1 px-3"}>
                              {triageDetails.manchester_triage.category || triageDetails.urgency.toUpperCase()}
                            </Badge>
                          </div>
                        </div>
                        
                        <div>
                          <h4 className="text-sm font-semibold uppercase text-gray-500 mb-1">Presenting Complaint</h4>
                          <p className="font-medium">{triageDetails.manchester_triage.presenting_complaint || 'Not specified'}</p>
                        </div>
                        
                        <div>
                          <h4 className="text-sm font-semibold uppercase text-gray-500 mb-1">Discriminator Used</h4>
                          <p className="font-medium">{triageDetails.manchester_triage.discriminator || 'Not specified'}</p>
                        </div>
                      </div>
                      
                      {triageDetails.manchester_triage.description && (
                        <div className="bg-slate-50 p-3 rounded border">
                          <h4 className="text-sm font-semibold uppercase text-gray-500 mb-1">Clinical Description</h4>
                          <p className="text-sm text-gray-700">{triageDetails.manchester_triage.description}</p>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              )}
              
              {/* Action Recommendation */}
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg">Action Recommendation</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {(() => {
                      const recommendation = getTriageActionRecommendation(triageDetails.urgency);
                      return (
                        <>
                          <div className="font-medium">{recommendation.action}</div>
                          <div className="flex items-center">
                            <Clock className="w-4 h-4 mr-1 text-amber-600" />
                            <span className="text-amber-600">Target time: {recommendation.time}</span>
                          </div>
                        </>
                      );
                    })()}
                  </div>
                </CardContent>
              </Card>
              

            
            {/* Action Recommendation */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg">Action Recommendation</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {(() => {
                    const recommendation = getTriageActionRecommendation(triageDetails.urgency);
                    return (
                      <>
                        <div className="font-medium">{recommendation.action}</div>
                        <div className="flex items-center">
                          <Clock className="w-4 h-4 mr-1 text-amber-600" />
                          <span className="text-amber-600">Target time: {recommendation.time}</span>
                        </div>
                      </>
                    );
                  })()}
                </div>
              </CardContent>
            </Card>
            
            {/* Vital Signs */}
            <Card className="overflow-hidden border-slate-200">
              <CardHeader className="pb-3 bg-slate-50">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Activity className="h-5 w-5 text-slate-700" />
                  Vital Signs
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <table className="w-full text-sm">
                  <thead className="bg-slate-100">
                    <tr>
                      <th className="py-2 px-4 text-left font-medium text-slate-600">Measurement</th>
                      <th className="py-2 px-4 text-left font-medium text-slate-600">Value</th>
                      <th className="py-2 px-4 text-left font-medium text-slate-600">Status</th>
                      <th className="py-2 px-4 text-left font-medium text-slate-600">Reference Range</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {formatVitalSigns(triageDetails.vitals).map((vital, index) => {
                      let range = '';
                      if (vital.name === 'Temperature') range = '36.0-38.0°C';
                      else if (vital.name === 'Heart Rate') range = '60-100 bpm';
                      else if (vital.name === 'Respiratory Rate') range = '12-24 rpm';
                      else if (vital.name === 'Oxygen Saturation') range = '≥94%';
                      
                      return (
                        <tr key={index} className="hover:bg-slate-50">
                          <td className="py-3 px-4 flex items-center gap-2">
                            <div className={getVitalSignStatusColor(vital.status)}>
                              {vital.icon}
                            </div>
                            <span className="font-medium">{vital.name}</span>
                          </td>
                          <td className="py-3 px-4 font-semibold">
                            {vital.value} {vital.unit}
                          </td>
                          <td className="py-3 px-4">
                            <Badge className={
                              vital.status === 'critical' ? 'bg-red-100 text-red-800 hover:bg-red-100' :
                              vital.status === 'warning' ? 'bg-amber-100 text-amber-800 hover:bg-amber-100' :
                              'bg-green-100 text-green-800 hover:bg-green-100'
                            }>
                              {vital.status.charAt(0).toUpperCase() + vital.status.slice(1)}
                            </Badge>
                          </td>
                          <td className="py-3 px-4 text-slate-600">{range}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </CardContent>
            </Card>
            
            {/* Symptoms and Red Flags in a clinical layout */}
            <div className="grid md:grid-cols-2 gap-4">
              {/* Glass Box AI Triage Explanation Panel */}
              <Card className="mb-4 border-l-4 border-l-blue-500">
                <CardHeader className="pb-2 bg-blue-50">
                  <CardTitle className="text-lg flex items-center gap-2 text-blue-800">
                    <Activity className="h-5 w-5" />
                    AI Triage Explanation
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-4">
                  <TriageExplanationPanel 
                    triageId={triageId || ''}
                    patientId={triageDetails.patient_id || ''}
                    triageData={{
                      symptoms: triageDetails.symptoms || [],
                      severity: triageDetails.severity || '',
                      duration: triageDetails.duration || '',
                      onset: triageDetails.onset || '',
                      additionalInfo: triageDetails.additional_info || '',
                      vitals: {
                        temperature: triageDetails.vitals?.temperature,
                        heartRate: triageDetails.vitals?.heart_rate,
                        bloodPressure: triageDetails.vitals?.blood_pressure,
                        respiratoryRate: triageDetails.vitals?.respiratory_rate,
                        oxygenSaturation: triageDetails.vitals?.oxygen_saturation,
                      },
                      age: triageDetails.patient_age,
                      gender: triageDetails.patient_gender,
                      isPregnant: triageDetails.is_pregnant || false,
                      smokingStatus: triageDetails.smoking_status,
                      medicalHistory: triageDetails.medical_history || [],
                      medications: triageDetails.medications || [],
                      allergies: triageDetails.allergies || []
                    }}
                    // triageId and patientId are already set above
                    isOverridden={!!triageDetails.override_urgency}
                    aiConfidence={triageDetails.ai_confidence} // Pass the AI confidence from triage submission
                    overrideUrgency={triageDetails.urgency} // Pass the stored urgency to ensure consistency
                    processedSymptoms={triageDetails.processed_symptoms} // Pass processed symptoms if available
                    symptomsWithSources={triageDetails.symptoms_with_sources} // Pass symptoms with sources if available
                  />
                </CardContent>
              </Card>

                {/* Patient Reported Symptoms */}
                <Card className="h-full">
                  <CardHeader className="pb-3 bg-slate-50">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <FileText className="h-5 w-5 text-slate-700" />
                      Reported Symptoms
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {/* Use processed_symptoms if available, fall back to original symptoms */}
                    {(triageDetails.processed_symptoms || triageDetails.symptoms || []).length === 0 ? (
                      <p className="text-gray-500 italic">No symptoms reported</p>
                    ) : (
                      <div className="space-y-2">
                        {/* If symptoms_with_sources is available, use it to show source information */}
                        {(triageDetails.symptoms_with_sources ? 
                          triageDetails.symptoms_with_sources : 
                          (triageDetails.processed_symptoms || triageDetails.symptoms || []).map(s => ({ name: s, source: 'explicit' as 'explicit' | 'extracted' })))
                          .map((symptom, index) => (
                          <div key={index} className={`flex items-start gap-2 border-b pb-2 ${
                            symptom.source === 'extracted' ? 'bg-amber-50' : ''
                          }`}>
                            <div className="min-w-5 pt-0.5">
                              <span className={`inline-flex items-center justify-center w-5 h-5 rounded-full ${
                                symptom.source === 'extracted' ? 'bg-amber-100 text-amber-800' : 'bg-blue-100 text-blue-800'
                              } text-xs font-medium`}>
                                {index + 1}
                              </span>
                            </div>
                            <p>
                              {symptom.name}
                              {symptom.source === 'extracted' && <span className="text-amber-600 ml-1">*</span>}
                            </p>
                          </div>
                        ))}
                        
                        {/* Add legend if there are extracted symptoms */}
                        {triageDetails.symptoms_with_sources?.some(s => s.source === 'extracted') && (
                          <div className="mt-2 text-xs text-amber-600">
                            * Symptom extracted from clinical notes
                          </div>
                        )}
                      </div>
                    )}
                  </CardContent>
                </Card>
                
                {/* Red Flags */}
                <Card className={triageDetails.red_flags && triageDetails.red_flags.length > 0 ? "border-red-200 h-full" : "h-full"}>
                  <CardHeader className={"pb-3 " + (triageDetails.red_flags && triageDetails.red_flags.length > 0 ? "bg-red-50" : "bg-slate-50")}>
                    <CardTitle className={"text-lg flex items-center gap-2 " + (triageDetails.red_flags && triageDetails.red_flags.length > 0 ? "text-red-600" : "")}>
                      <AlertTriangle className="w-5 h-5" />
                      Red Flags
                      {triageDetails.red_flags && triageDetails.red_flags.length > 0 && (
                        <span className="ml-1 inline-flex items-center justify-center w-5 h-5 rounded-full bg-red-100 text-red-800 text-xs font-medium">
                          {triageDetails.red_flags.length}
                        </span>
                      )}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {!triageDetails.red_flags || triageDetails.red_flags.length === 0 ? (
                      <p className="text-gray-500 italic">No red flags identified</p>
                    ) : (
                      <div className="space-y-2">
                        {triageDetails.red_flags.map((flag, index) => (
                          <div key={index} className="flex items-start gap-2 border-b border-red-100 pb-2">
                            <AlertTriangle className="h-5 w-5 text-red-600 min-w-5 mt-0.5" />
                            <p className="text-red-700">{flag}</p>
                          </div>
                        ))}
                        <div className="bg-red-50 p-3 rounded-md mt-2 text-sm text-red-800">
                          <p className="font-semibold">Clinical Alert:</p>
                          <p>These red flags require immediate clinical attention according to triage protocol.</p>
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
              
              {/* AI Confidence - Enhanced with interpretive scale */}
              {triageDetails.ai_confidence && (
                <Card className="overflow-hidden">
                  <CardHeader className="pb-3 bg-slate-50">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-lg flex items-center gap-2">
                        <Activity className="h-5 w-5 text-blue-600" />
                        AI Assessment Confidence
                      </CardTitle>
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger>
                            <Info className="h-5 w-5 text-gray-500" />
                          </TooltipTrigger>
                          <TooltipContent className="max-w-sm p-4">
                            <p>This score represents the AI system's confidence in its triage assessment. Higher values indicate greater confidence in the urgency level assignment.</p>
                            <div className="mt-2 text-xs">
                              <div className="flex items-center"><span className="w-3 h-3 bg-red-500 inline-block mr-1 rounded-sm"></span> &lt;70%: Low confidence</div>
                              <div className="flex items-center"><span className="w-3 h-3 bg-yellow-500 inline-block mr-1 rounded-sm"></span> 70-85%: Moderate confidence</div>
                              <div className="flex items-center"><span className="w-3 h-3 bg-green-500 inline-block mr-1 rounded-sm"></span> &gt;85%: High confidence</div>
                            </div>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </div>
                  </CardHeader>
                  <CardContent className="p-4">
                    <div className="space-y-3">
                      <div className="flex items-center">
                        <div className="w-full bg-gray-200 rounded-full h-3">
                          <div 
                            className={`h-3 rounded-full ${triageDetails.ai_confidence < 0.7 ? 'bg-red-500' : triageDetails.ai_confidence < 0.85 ? 'bg-yellow-500' : 'bg-green-500'}`}
                            style={{ width: `${Math.min(100, Math.max(0, triageDetails.ai_confidence * 100))}%` }}
                          ></div>
                        </div>
                        <div className="ml-3 font-bold text-lg" style={{ color: triageDetails.ai_confidence < 0.7 ? '#ef4444' : triageDetails.ai_confidence < 0.85 ? '#eab308' : '#22c55e' }}>
                          {Math.round(triageDetails.ai_confidence * 100)}%
                        </div>
                      </div>
                      <div className="text-sm text-gray-600 bg-blue-50 p-3 rounded border border-blue-100">
                        <p className="font-medium text-blue-800">Clinical Interpretation:</p>
                        <p>
                          {triageDetails.ai_confidence < 0.7 
                            ? 'Low confidence score suggests human clinical judgment is strongly recommended for this case.'
                            : triageDetails.ai_confidence < 0.85
                            ? 'Moderate confidence score indicates the AI assessment is likely accurate but human verification is advised.'
                            : 'High confidence score suggests the AI assessment is highly reliable for this presentation.'}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}
              
              {/* Doctor Notes - Enhanced with clinical styling */}
              <Card className="overflow-hidden">
                <CardHeader className="pb-3 bg-slate-50">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <FileText className="h-5 w-5 text-slate-700" />
                    Clinical Notes
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-4">
                  <div className="space-y-3">
                    <div className="flex justify-between items-center text-sm text-gray-500 mb-1">
                      <span className="font-medium">Doctor's Assessment & Plan</span>
                      {triageDetails.doctor_notes && (
                        <span className="text-xs">Last updated: {format(new Date(), 'MMM dd, yyyy h:mm a')}</span>
                      )}
                    </div>
                    <Textarea 
                      placeholder="Enter your clinical assessment, management plan, and any other relevant notes here..." 
                      className="min-h-[150px] font-mono text-sm" 
                      value={doctorNotes}
                      onChange={(e) => setDoctorNotes(e.target.value)}
                    />
                    <div className="flex justify-between">
                      <p className="text-xs text-gray-500">All notes are saved to the patient's electronic health record</p>
                      <Button 
                        className="gap-1" 
                        onClick={handleSaveNotes}
                      >
                        <CheckCircle className="h-4 w-4" />
                        Save Clinical Notes
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              {/* Doctor Assignment - Enhanced with clinical styling */}
              {(!triageDetails.assigned_doctor_id || triageDetails.status !== 'reviewed') && (
                <Card className="overflow-hidden border-blue-200">
                  <CardHeader className="pb-3 bg-blue-50">
                    <CardTitle className="text-lg flex items-center gap-2 text-blue-800">
                      <Stethoscope className="h-5 w-5" />
                      Case Assignment
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-4">
                    <div className="space-y-3">
                      <p className="text-sm text-gray-700">
                        Assign a physician to review this triage assessment and provide clinical care.
                      </p>
                      <div className="flex flex-col md:flex-row items-start md:items-center gap-3">
                        <div className="flex-grow space-y-1">
                          <label className="text-sm font-medium text-gray-700">Attending Physician</label>
                          <Select value={selectedDoctor} onValueChange={setSelectedDoctor}>
                            <SelectTrigger className="w-full">
                              <SelectValue placeholder="Select a doctor" />
                            </SelectTrigger>
                            <SelectContent>
                              {doctors.map((doctor) => (
                                <SelectItem key={doctor.id} value={doctor.id}>
                                  {doctor.name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="flex justify-end mt-4">
                          <Button 
                            className="gap-2" 
                            onClick={handleAssignDoctor}
                            disabled={!selectedDoctor || isAssigning}
                          >
                            {isAssigning ? (
                              <>
                                <Loader2 className="h-4 w-4 animate-spin" />
                                Assigning...
                              </>
                            ) : (
                              <>
                                <UserCheck className="h-4 w-4" />
                                Assign Doctor
                              </>
                            )}
                          </Button>
                        </div>
                      </div>
                      <div className="text-xs text-gray-500 bg-blue-50 p-2 rounded">
                        Assignment will update the case status and notify the selected physician.
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          )}
        </ScrollArea>
        
        <DialogFooter className="border-t p-4 bg-slate-50">
          <div className="flex w-full justify-between items-center">
            <div className="text-xs text-gray-500">
              {triageDetails && <span>Case ID: {triageId?.substring(0, 8)}</span>}
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={onClose} className="gap-1">
                Close
              </Button>
              {triageDetails && (
                <Button onClick={handleDownloadSummary} className="gap-2 bg-green-600 hover:bg-green-700">
                  <Download className="h-4 w-4" />
                  Download Clinical Summary
                </Button>
              )}
            </div>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}